import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import { PerspectiveCard } from "../cards/PerspectiveCard";
import { StackedKpiInitiative } from "../charts/StackedKpiInitiative";
import { IChartSelectEvent } from "../charts/types";
import PerspectiveHeader from "../texts/PerspectiveHeader";
import PerspectiveName from "../texts/PerspectiveName";

const useElementOverviewStackedCardStyles = makeThemedStyles(
  "ElementOverviewStackedCard",
  (theme) => ({
    root: {
      width: "30.4em",
      minHeight: "37.1em",
      fontSize: "10px",
      borderRadius: ".8em",
      border: "1px solid rgba(247, 247, 247, 1)",
    },
    divider: {
      height: "20px",
    },
    perfMetrics: {
      marginTop: "0.6em",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: "0.25em",
      fontSize: "12px",
      lineHeight: 1.35,
      color: "rgba(16, 41, 117, 0.72)",
    },
    perfMetricRow: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "baseline",
      columnGap: "0.35em",
      rowGap: "0.1em",
    },
    perfMetricLabel: {
      fontWeight: 500,
    },
    perfMetricValue: {
      fontWeight: 600,
      color: "rgba(16, 41, 117, 1)",
    },
  })
);

export const ElementOverviewStackedCard = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren;
      translatedName: string;
      periodName: string;
      title: string;
      typenames: string[];
      onSelect?: (value: IChartSelectEvent) => void;
    }>
  ) => {
    const { element: el } = props;
    const styles = useElementOverviewStackedCardStyles();
    const ytdPretty = el.PERF_YTD_prettyvalue?.trim();
    const perfPretty = el.SCORE_YTD_valuewithoutsymbol?.trim();
    const showPerfMetrics = Boolean(ytdPretty || perfPretty);
    return (
      <PerspectiveCard>
        <PerspectiveHeader title={props.title} periodName={props.periodName} />
        {showPerfMetrics ? (
          <div className={styles.perfMetrics}>
            {ytdPretty ? (
              <div className={styles.perfMetricRow}>
                <span className={styles.perfMetricLabel}>{t("overview.Performance")} : </span>
                <span className={styles.perfMetricValue}>{ytdPretty}</span>
              </div>
            ) : null}
            {perfPretty ? (
              <div className={styles.perfMetricRow}>
                <span className={styles.perfMetricLabel}>{t("overview.Score")} : </span>
                <span className={styles.perfMetricValue}>{perfPretty}</span>
              </div>
            ) : null}
          </div>
        ) : null}
        <PerspectiveName>{props.translatedName}</PerspectiveName>
        <div className={styles.divider} />
        <StackedKpiInitiative
          element={flattenHierarchy(el.children).filter(
            (ch) => props.typenames.includes(ch.typename) && ch.STATUS === "1"
          )}
          perfColorKey="rangecolor"
          perfNameKey="rangename"
          typenames={props.typenames}
          onSelect={props.onSelect}
        />
      </PerspectiveCard>
    );
  }
);
